import { Dayjs } from 'dayjs';
import { Nullable } from './index';
import { RevolvingCreditLineClient, RevolvingCreditLineClientDetail } from './servicing';

export enum CreditLineProductType {
  REVOLVING = 'revolving',
  FACTORING = 'factoring',
}

export enum FactoringInvoiceState {
  AVAILABLE = 'available',
  SELECTED = 'selected',
  SOLD = 'sold',
  EXPIRED = 'expired',
}

export enum FactoringEligibilityFilter {
  ALL = 'all',
  ELIGIBLE = 'eligible',
  INELIGIBLE = 'ineligible',
}

// Restricts the invoices list to the ones Marco actually bought (sold + expired). Used by the
// read-only "Invoices" history screen; absent on the sell screen (which lists the raw SAT feed).
export enum FactoringInvoiceScope {
  HISTORY = 'history',
}

export enum FactoringInvoiceStatusVariant {
  SELL = 'sell',
  HISTORY = 'history',
}

// History sub-filter: `active` = sold and still in a live group, `expired` = its group expired.
export enum FactoringHistoryStateFilter {
  ALL = 'all',
  ACTIVE = 'active',
  EXPIRED = 'expired',
}

// Server-side invoice status filter (`status` query param); omit the param to list all.
export enum FactoringInvoiceStatusFilter {
  ALL = 'all',
  AVAILABLE = 'available',
  SOLD = 'sold',
  EXPIRED = 'expired',
  NOT_ADVANCED = 'not_advanced',
  INELIGIBLE = 'ineligible',
}

export enum FactoringExtractionState {
  INCOMPLETE = 'incomplete',
}

export type FactoringInvoice = {
  id: string;
  sourceInvoiceId: string;
  status: string;
  issuedAt: Nullable<string>;
  soldAt: Nullable<string>;
  expiryDate: Nullable<string>;
  paymentType: Nullable<string>;
  issuerRfc: string;
  issuerName: Nullable<string>;
  receiverRfc: string;
  receiverName: Nullable<string>;
  currency: string;
  total: string;
  paidAmount: string;
  dueAmount: Nullable<string>;
  pdfUrl: Nullable<string>;
  xmlUrl: Nullable<string>;
  isEligible: boolean;
  rejectReasons: string[];
  factoringState: FactoringInvoiceState;
  recommended: boolean;
  // USD collateral this invoice contributes (0 when not advanced).
  collateralUsd: string;
  // Not-advanced overlay (client-visible part): the flag and the justification
  // meant for the client. The admin-only reason lives on AdminFactoringInvoice.
  notAdvanced: boolean;
  notAdvancedClientReason: Nullable<string>;
};

export type FactoringInvoicesFilters = {
  // Zero-based page (Table convention); mapped to the 1-based `page` param in the service.
  page: number;
  pageSize: number;
  search: string;
  eligibility: FactoringEligibilityFilter;
  // History screen only: restrict to invoices Marco bought and optionally to active/expired ones.
  scope?: FactoringInvoiceScope;
  state?: FactoringHistoryStateFilter;
  status?: FactoringInvoiceStatusFilter;
};

export type FactoringExtractionStatus = {
  invoiceCount: number;
  eligibleCount: number;
  complete: boolean;
  status: Nullable<string>;
  failed: boolean;
  errorReason: Nullable<string>;
  lastEventAt: Nullable<string>;
  // False once the company used its manual SAT refresh for the day (1 manual +
  // 1 automatic pull per day: each Syntage pull is billable).
  manualRefreshAvailable: boolean;
};

export type FactoringCollateralSync = {
  collateralUsd: string;
  timestamp: Nullable<number>;
};

export type FactoringSoldSummary = {
  soldCount: number;
  hasEverSold: boolean;
};

export type SellFactoringInvoicesPayload = {
  invoiceIds: string[];
  termsAccepted: boolean;
};

export type SellFactoringInvoicesResponse = {
  saleReference: string;
  soldInvoiceIds: number[];
  soldCount: number;
  rejected: { invoiceId: number; reason: string | null }[];
  collateralUsd: string | null;
  rclCreditLineId: string;
};

// A client's request to enable the factoring credit line (no amount — see service).
export type FactoringProductRequest = {
  id: number;
  company: number;
  createdAt?: string;
};

// Factoring credit lines live in RCL: same endpoints as the revolving product,
// with the extra fields added by the factoring serializer changes (plan §2.5).
// Availability = min(limit, collateral) - totalDebt and can be negative; availableToDraw is max(0, availability).
export type FactoringCollateralFields = {
  collateral: string;
  collateralTimestamp: Nullable<number>;
  availability: string;
  availableToDraw: string;
};

export type FactoringCreditLineClient = RevolvingCreditLineClient &
  FactoringCollateralFields & {
    productType: CreditLineProductType;
  };

export type FactoringCreditLineDetail = RevolvingCreditLineClientDetail &
  FactoringCollateralFields & {
    productType: CreditLineProductType;
    creditUtilized: string;
  };

// Admin view of a sold CFDI (Platform admin sold-invoices endpoint, Contract G).
export type AdminFactoringSoldInvoice = {
  sourceInvoiceId: string;
  receiverRfc: string;
  receiverName: Nullable<string>;
  currency: string;
  total: string;
  soldAt: Nullable<string>;
  factoringState: FactoringInvoiceState;
  isExpired: boolean;
  collateralUsd: string;
};

// Admin cross-company invoices tab: the full invoice plus the owning company and
// the credit line it was sold under. Carries everything the detail drawer renders.
export type AdminFactoringInvoice = FactoringInvoice & {
  companyId: number;
  companyName: Nullable<string>;
  rclCreditLineId: Nullable<string>;
  isExpired: boolean;
  // Admin-only justification (never exposed to the client).
  notAdvancedAdminReason: Nullable<string>;
};

export type InvoicesTabState = {
  filters: {
    search: string;
    rclCreditLineId: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    status: FactoringInvoiceStatusFilter;
  };
  currentPage: number;
  pageSize: number;
};
